import {
    AfterContentInit,
    Component,
    ElementRef,
    HostBinding,
    Input,
    OnChanges,
    Renderer2,
    SimpleChanges,
    ViewEncapsulation,
    Self,
    Optional,
    DoCheck,
    OnDestroy,
} from '@angular/core';
import { NgControl, SelectControlValueAccessor, SelectMultipleControlValueAccessor } from '@angular/forms';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { takeUntil, filter, startWith } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'select[euiSelect]',
    styleUrls: ['./styles/_index.scss'],
    template: '<ng-content></ng-content>',
    encapsulation: ViewEncapsulation.None,
})
export class EuiSelectComponent implements AfterContentInit, OnChanges, DoCheck, OnDestroy {

    @Input()
    public get isInvalid() {
        return this._isInvalid || null;
    }
    public set isInvalid(state: any) {
        this.setInvalid(state);
    }
    protected _isInvalid: boolean;

    @HostBinding('attr.multiple')
    @Input()
    public get multiple(): boolean {
        return this._multiple;
    }
    public set multiple(value: boolean|null) {
        this._multiple = coerceBooleanProperty(value) ? true : undefined;
    }

    @HostBinding('attr.id')
    @Input()
    public get id(): string {
        return this._id;
    }
    public set id(value: string|null) {
        this.setIdAttribute(value);
    }

    @HostBinding('class')
    public get cssClasses(): any {
        return this.getCssClasses();
    }

    @Input()
    public get readonly() {
        return this._readonly;
    }
    public set readonly(state: any) {
        this._readonly = coerceBooleanProperty(state);
        if (this._readonly) {
            this.renderer.setAttribute(this.element, 'readonly', 'true');
        } else {
            this.renderer.removeAttribute(this.element, 'readonly');
        }
    }
    protected _readonly: boolean;

    @Input() placeholder: string;
    protected _placeholder: HTMLOptionElement;
    protected element: HTMLSelectElement;
    protected renderer: Renderer2;
    protected _id: string;
    protected _multiple: any;
    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(private _renderer: Renderer2,
                private _elementRef: ElementRef,
                @Optional() @Self() private control: NgControl) {
        this.element = _elementRef.nativeElement;
        this.renderer = _renderer;
        // set random id
        this.setIdAttribute();
    }

    ngAfterContentInit() {
        const select: HTMLSelectElement = this._elementRef.nativeElement;

        if (this.control) {
            // listen to value changes for undefined and set it to placeholder if that exists.
            this.control.valueChanges.pipe(
                takeUntil(this.destroy$),
                startWith(this.control.value),
                filter( () => this._placeholder !== undefined)
            ).subscribe(value => {
                if (value === undefined) {
                    select.options[0].selected = true;
                }
                if (value !== undefined) {
                    select.options[0].innerText = '';
                }
                if (value === this._placeholder.value) {
                    this.control.control.setValue(undefined);
                    select.options[0].innerText = this.placeholder;
                }
            });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['placeholder']) {
            // in case option Element already created update the value
            if (this._placeholder) {
                this.renderer.setProperty(this._placeholder, 'innerText', changes['placeholder'].currentValue);
            } else {
                // create option Element
                this._placeholder = this.renderer.createElement('option');
                this.renderer.setProperty(this._placeholder, 'innerText', changes['placeholder'].currentValue);
                // set attributes to act as a placeholder
                // this.renderer.setAttribute(this._placeholder, 'disabled', '');
                if (this.control && !this.control?.value) {
                    this.renderer.setAttribute(this._placeholder, 'selected', '');
                } else if (!this.control) {
                    // if no control exists then check if any other option is already
                    // selected otherwise set placeholder as selected.
                    const select: HTMLSelectElement = this._elementRef.nativeElement;
                    // check against element attribute (and not JS Object attribute)
                    const selected = Array.from(select.options).find(i => i.hasAttribute('selected') === true);

                    // if none is selected then placeholder must be selected
                    if (!selected) {
                        this.renderer.setAttribute(this._placeholder, 'selected', '');
                    }
                }
                // append option element as the first of the select children
                const parent: HTMLElement = this.element;
                parent.insertBefore(this._placeholder, parent.firstChild);

                if (this.control && !this.control?.value) {
                    this.control.valueAccessor.writeValue(undefined);
                    this.control.control.setValue(undefined);
                }
            }
        }

        if (changes['multiple'] && this.control) {
            if (!this._multiple) {
                this.control.valueAccessor = new SelectControlValueAccessor(this._renderer, this._elementRef);
            } else {
                this.control.valueAccessor = new SelectMultipleControlValueAccessor(this._renderer, this._elementRef);
            }
        }
    }

    ngDoCheck() {
        if (this.control) {
            this._isInvalid = this.control.invalid && this.control.touched;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.complete();
        this.destroy$.unsubscribe();
    }

    /**
     * calculates and set's the invalid state based on the provided one. If NG_VALUE_ACCESSOR is provided
     * then control invalid attribute has priority.
     * @param state
     * @protected
     */
    protected setInvalid(state?: any): void {
        // in case it's controlled by NgControl override
        this._isInvalid = this.control ? this.control.invalid && this.control.touched : coerceBooleanProperty(state);
    }

    /**
     * returns string of classes
     * @protected
     */
    protected getCssClasses(): string {
        return [
            'eui-select',
            this._isInvalid ? `eui-select--invalid` : '',
        ].join(' ').trim();
    }

    /**
     * set the id attribute of the element
     * @param value the ID attribute (https://www.w3schools.com/html/html_id.asp)
     * @protected
     */
    protected setIdAttribute(value: string|null = null) {
        this._id = value ? value : `select_${Math.floor(Math.random() * 1000000)}`;
    }
}
